import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { Stroke } from '../types.ts';

export type Tool = Stroke['tool'] | 'eraser';

/**
 * Draws one stroke into a 2D context. Coordinates are normalised 0..1, so the same stroke
 * renders identically over a 4K frame, a 1080p proxy, or the flattened export PNG.
 */
function paintStroke(ctx: CanvasRenderingContext2D, stroke: Stroke, w: number, h: number): void {
  const pts = stroke.points;
  if (pts.length === 0) return;

  const px = (p: [number, number]): [number, number] => [p[0] * w, p[1] * h];

  ctx.save();
  ctx.strokeStyle = stroke.color;
  ctx.fillStyle = stroke.color;
  // Scale line width with the canvas so a 3px pen looks the same at any size.
  ctx.lineWidth = (stroke.width * h) / 1080;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const first = pts[0];
  const last = pts[pts.length - 1];
  if (!first || !last) {
    ctx.restore();
    return;
  }

  switch (stroke.tool) {
    case 'pen': {
      ctx.beginPath();
      const [x0, y0] = px(first);
      ctx.moveTo(x0, y0);
      for (const p of pts.slice(1)) {
        const [x, y] = px(p);
        ctx.lineTo(x, y);
      }
      ctx.stroke();
      break;
    }
    case 'line': {
      const [x0, y0] = px(first);
      const [x1, y1] = px(last);
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
      break;
    }
    case 'arrow': {
      const [x0, y0] = px(first);
      const [x1, y1] = px(last);
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
      // Head length follows the line width so it stays proportional at every zoom level.
      const head = Math.max(ctx.lineWidth * 4, 8);
      const angle = Math.atan2(y1 - y0, x1 - x0);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 - head * Math.cos(angle - Math.PI / 6), y1 - head * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(x1 - head * Math.cos(angle + Math.PI / 6), y1 - head * Math.sin(angle + Math.PI / 6));
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'rect': {
      const [x0, y0] = px(first);
      const [x1, y1] = px(last);
      ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
      break;
    }
    case 'ellipse': {
      const [x0, y0] = px(first);
      const [x1, y1] = px(last);
      ctx.beginPath();
      ctx.ellipse(
        (x0 + x1) / 2,
        (y0 + y1) / 2,
        Math.abs(x1 - x0) / 2,
        Math.abs(y1 - y0) / 2,
        0,
        0,
        Math.PI * 2,
      );
      ctx.stroke();
      break;
    }
  }
  ctx.restore();
}

/** Flattens strokes to a transparent PNG at the clip's native resolution, for export. */
export function strokesToPng(strokes: Stroke[], width: number, height: number): string | undefined {
  if (strokes.length === 0) return undefined;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;
  for (const stroke of strokes) paintStroke(ctx, stroke, width, height);
  return canvas.toDataURL('image/png');
}

/**
 * A downscaled snapshot of what the player is showing right now, as an opaque canvas. This is
 * the background a drawing gets flattened onto so the notes thumbnail shows the frame, not just
 * red lines on black. Capped at 960px because it only ever feeds a thumbnail. Returns null when
 * the video is not ready or is cross-origin (which would taint the canvas) — the caller then
 * falls back to the transparent strokes-only PNG.
 */
export function captureVideoFrame(video: HTMLVideoElement | null): HTMLCanvasElement | null {
  if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return null;
  const scale = Math.min(1, 960 / video.videoWidth);
  const w = Math.max(1, Math.round(video.videoWidth * scale));
  const h = Math.max(1, Math.round(video.videoHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    ctx.drawImage(video, 0, 0, w, h);
  } catch {
    return null;
  }
  return canvas;
}

/** Flattens strokes over a captured frame to an opaque PNG — the note thumbnail with its image. */
export function flattenToPng(background: HTMLCanvasElement, strokes: Stroke[]): string | undefined {
  const canvas = document.createElement('canvas');
  canvas.width = background.width;
  canvas.height = background.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;
  ctx.drawImage(background, 0, 0);
  for (const stroke of strokes) paintStroke(ctx, stroke, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

interface Props {
  /** Strokes being drawn right now by this user. */
  strokes: Stroke[];
  onStrokesChange: (strokes: Stroke[]) => void;
  /** Saved or remote strokes, shown but not editable. */
  overlayStrokes?: Stroke[];
  enabled: boolean;
  tool: Tool;
  color: string;
  lineWidth: number;
  /** Called with the finished stroke so it can be relayed to the room live. */
  onStrokeComplete?: (stroke: Stroke) => void;
  /** Same pan/zoom transform as the video, so drawings stay glued to the image. */
  style?: React.CSSProperties;
  /** Width / height of the clip. The canvas covers exactly the picture inside the letterbox. */
  aspect?: number | null;
  /** Side by side: the picture sits in the left half of the stage, not across all of it. */
  leftHalf?: boolean;
}

interface Fit {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Where the picture actually is inside a box of this size — the same arithmetic as the
 * video's `object-fit: contain`. Without an aspect, the whole box.
 */
function fitInside(boxW: number, boxH: number, aspect: number | null | undefined): Fit {
  if (!aspect || !Number.isFinite(aspect) || aspect <= 0 || boxW <= 0 || boxH <= 0) {
    return { left: 0, top: 0, width: boxW, height: boxH };
  }
  const width = Math.min(boxW, boxH * aspect);
  const height = width / aspect;
  return { left: (boxW - width) / 2, top: (boxH - height) / 2, width, height };
}

function AnnotationCanvasImpl({
  strokes,
  onStrokesChange,
  overlayStrokes = [],
  enabled,
  tool,
  color,
  lineWidth,
  onStrokeComplete,
  style,
  aspect,
  leftHalf = false,
}: Props): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);
  // The picture's rectangle inside the layer. Strokes are stored relative to the picture, not
  // to the stage: a window resized to another shape moves the letterbox bars, and drawings
  // measured against the stage would slide off the image with them.
  const [fit, setFit] = useState<Fit | null>(null);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    const measure = (): void => {
      // client sizes ignore the zoom transform on purpose — the fit is in unzoomed layout space.
      const next = fitInside(layer.clientWidth, layer.clientHeight, aspect);
      setFit((prev) =>
        prev &&
        Math.abs(prev.left - next.left) < 0.5 &&
        Math.abs(prev.top - next.top) < 0.5 &&
        Math.abs(prev.width - next.width) < 0.5 &&
        Math.abs(prev.height - next.height) < 0.5
          ? prev
          : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(layer);
    return () => observer.disconnect();
  }, [aspect]);
  const drawing = useRef<Stroke | null>(null);
  const strokesRef = useRef(strokes);
  strokesRef.current = strokes;

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const stroke of overlayStrokes) paintStroke(ctx, stroke, canvas.width, canvas.height);
    for (const stroke of strokesRef.current) paintStroke(ctx, stroke, canvas.width, canvas.height);
    if (drawing.current) paintStroke(ctx, drawing.current, canvas.width, canvas.height);
  }, [overlayStrokes]);

  // Match the backing store to the displayed size so lines are not blurry on HiDPI screens.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(1, Math.round(rect.width * dpr));
      const h = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      redraw();
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [redraw]);

  useEffect(redraw, [redraw, strokes, overlayStrokes]);

  const pointAt = (event: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const rect = event.currentTarget.getBoundingClientRect();
    return [
      Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    ];
  };

  const hitTest = (strokeList: Stroke[], point: [number, number]): number => {
    // Erase by proximity to any point of a stroke — simple and predictable in practice.
    const threshold = 0.02;
    for (let i = strokeList.length - 1; i >= 0; i -= 1) {
      const stroke = strokeList[i];
      if (!stroke) continue;
      for (const p of stroke.points) {
        if (Math.hypot(p[0] - point[0], p[1] - point[1]) < threshold) return i;
      }
    }
    return -1;
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    // Only the primary button draws. The middle button belongs to the stage (it pans), so it is
    // left to bubble up there instead of being captured as the start of a stroke.
    if (!enabled || event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = pointAt(event);

    if (tool === 'eraser') {
      const index = hitTest(strokesRef.current, point);
      if (index >= 0) {
        const next = strokesRef.current.filter((_, i) => i !== index);
        onStrokesChange(next);
      }
      return;
    }

    drawing.current = { tool, color, width: lineWidth, points: [point] };
    redraw();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!enabled || !drawing.current) return;
    const point = pointAt(event);
    if (drawing.current.tool === 'pen') {
      drawing.current.points.push(point);
    } else {
      // Shapes are defined by their first and last point only.
      drawing.current.points = [drawing.current.points[0] ?? point, point];
    }
    redraw();
  };

  const onPointerUp = (): void => {
    if (!enabled || !drawing.current) return;
    const finished = drawing.current;
    drawing.current = null;
    // A click without a drag is not a shape.
    if (finished.points.length < 2 && finished.tool !== 'pen') {
      redraw();
      return;
    }
    onStrokesChange([...strokesRef.current, finished]);
    onStrokeComplete?.(finished);
  };

  // The layer spans the stage (or its left half) and carries the pan/zoom transform, exactly
  // like the video element does; the canvas inside it sits on the picture.
  return (
    <div
      ref={layerRef}
      className={`annotation-layer${leftHalf ? ' annotation-layer--left' : ''}`}
      style={style}
    >
      <canvas
        ref={canvasRef}
        className={`annotation-canvas${enabled ? ' is-drawing' : ''}`}
        style={fit ? { left: fit.left, top: fit.top, width: fit.width, height: fit.height } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  );
}

/**
 * Memoised in its own right: the stage still re-renders while the wipe handle is dragged,
 * and repainting the canvas for that would be pure waste.
 */
export const AnnotationCanvas = memo(AnnotationCanvasImpl);
